import {
  type AppointmentLocationType,
  type AppointmentPaymentStatus,
  type AppointmentReasonCode,
  type AppointmentStatus,
  type OpportunityDrawing,
  type ParentTaskType,
  type QuestionsInfo,
  type ResponseStatus,
  type SubTaskType,
  type Task,
} from "@repo/api";

interface CoWorkerCalendarBase {
  id: string;
  emailId: string;
  assignedTeam?: number;
  allDay: boolean;
  isDueBy?: boolean;
  start: Date;
  end: Date;
  displayStart?: Date;
  title: string;
  defaultTitle?: boolean;
  description?: string;
  length: number;
  customer?: {
    firstName?: string;
    lastName: string;
  };
}

export interface CoWorkerCalendarBokaAppointment extends CoWorkerCalendarBase {
  appointmentType: "appointment";
  locationType: AppointmentLocationType;
  active: boolean;
  opportunityId?: number;
  opportunityTitle?: string;
  tentative?: boolean;

  // ---- Appointment specific
  reasonCode?: AppointmentReasonCode;
  status?: AppointmentStatus;
  service?: string;
  plannerOpportunities?: OpportunityDrawing[];
  opportunityDetail?: {
    estimatedBudget?: string;
    expectedPurchase?: string;
    followUpConsent?: boolean;
  };
  tasks?: Task[];
  location?: string;
  paymentStatus?: AppointmentPaymentStatus;
  questionsInfo?: QuestionsInfo;
  internal?: boolean;
  walkIn?: boolean;
  orderNumber?: string;
  videoLinkCoworker?: string;
  isDropIn?: boolean;
}

export interface CoWorkerCalendarTask extends CoWorkerCalendarBase {
  appointmentType: "task";
  parentType: ParentTaskType;
  subType: SubTaskType;
  active: boolean;
  opportunityId?: number;
  opportunityTitle?: string;
}

export interface CoWorkerCalendarOutlook extends CoWorkerCalendarBase {
  appointmentType: "outlook";
  active: boolean;
  showAs?: OutlookEvent["showAs"];
  sensitivity?: OutlookEvent["sensitivity"];
  attendees?: OutlookEvent["attendees"];
  onlineMeeting?: OutlookEvent["onlineMeeting"];
  location?: OutlookEvent["location"];
  responseStatus?: ResponseStatus;
  isCancelled?: boolean;
  webLink?: string;
}

export interface CoWorkerScheduleActivity extends CoWorkerCalendarBase {
  appointmentType: "scheduleActivity";
  parentType: ParentTaskType;
  subType: SubTaskType;
  shiftType?: string;
  activity?: string;
  editedAt?: string;
  editedBy?: string;
}

export interface OutlookEvent {
  id: string;
  subject: string;
  showAs:
    "free" | "tentative" | "busy" | "oof" | "workingElsewhere" | "unknown";
  isAllDay: boolean;
  sensitivity: "normal" | "private";
  createdDateTime: string;
  bodyPreview: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  isOnlineMeeting: boolean;
  webLink?: string;
  location?: {
    displayName: string;
  };
  attendees?: [
    {
      type: "required" | "optional";
      status?: {
        response: ResponseStatus;
      };
      emailAddress?: {
        name: string;
        address?: string;
      };
    },
  ];
  onlineMeeting?: {
    joinUrl?: string;
  };
  responseStatus?: {
    response?: ResponseStatus;
  };
  isCancelled?: boolean;
}

export type CalendarZoom = "NORMAL" | "ZOOM_OUT";

export type CalendarView = "AGENDA" | "OVERVIEW" | "TEAM";

export type CoWorkerCalendarItem =
  | CoWorkerCalendarBokaAppointment
  | CoWorkerCalendarOutlook
  | CoWorkerCalendarTask
  | CoWorkerScheduleActivity;

export const isCoWorkerScheduleActivity = (
  item: CoWorkerCalendarItem
): item is CoWorkerScheduleActivity => {
  if (item.appointmentType === "scheduleActivity") {
    return true;
  }

  return false;
};

export const isCoWorkerCalendarTask = (
  item: CoWorkerCalendarItem
): item is CoWorkerCalendarTask => {
  if (item.appointmentType === "task") {
    return true;
  }

  return false;
};

export const isCoWorkerCalendarOutlook = (
  item: CoWorkerCalendarItem
): item is CoWorkerCalendarOutlook => {
  if (item.appointmentType === "outlook") {
    return true;
  }

  return false;
};

export const isCoWorkerCalendarBokaAppointment = (
  item: CoWorkerCalendarItem
): item is CoWorkerCalendarBokaAppointment => {
  if (item.appointmentType === "appointment") {
    return true;
  }

  return false;
};

export const isAwayTask = (item: CoWorkerCalendarTask): boolean =>
  item.parentType === "away";

export const isAbsenceScheduleActivity = (
  item: CoWorkerScheduleActivity
): boolean =>
  item.shiftType?.toLowerCase() === "absence" ||
  item.shiftType?.toLowerCase() === "break";
